import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Loader from '@/components/ui/loader'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import {
  TanCreateRequest,
  TanCreateResponse,
  TanConfirmRequest
} from '@/services/mobi-api/handsetDti/types'
import {
  createTanHandset,
  confirmTanHandset
} from '@/services/mobi-api/handsetDti'
import useIntl from '@/utils/intl'
import { Loop } from '@mui/icons-material'
import { Divider, TextField } from '@mui/material'
import React, { FunctionComponent, useEffect, useReducer, useRef } from 'react'
import OTPInput from 'react-otp-input'
import { Obj } from '..'
import EMongolia from '@/pages/handsetDti/eMongolia'
import EmailInput from './emailInput'

interface Props {
  onClick: (obj: Obj) => void
  back: () => void
  instanceId: string
  initialPhoneNumber?: string
  choosenPackage?: string
}

interface State {
  loading: boolean
  otp: string
  phoneNumber: string
  email: string
  tanLocked: boolean
  tanIdentifierLocked: boolean
  isTanCreated: boolean
  tanRes?: TanCreateResponse['response']
  counter: number
  timerId?: NodeJS.Timer
  errorMessage?: string
  danRedirectUrl?: string
  needsEmailInput: boolean
  showEmailInput: boolean
  isEmailTanCreated: boolean
}

const checkPhoneNumber = (phone: string | undefined | null): boolean => {
  if (typeof phone !== 'string') return false
  const phoneRegex = /^[89]\d{7}$/
  return phoneRegex.test(phone.replace(/\D/g, ''))
}

const NumberInput: FunctionComponent<Props> = ({
  onClick,
  back,
  instanceId,
  initialPhoneNumber,
  choosenPackage
}) => {
  const { translate } = useIntl()
  const { showError } = useToast()
  const { locale } = useLanguage()
  const [counter, setCounter] = React.useState(0)
  const [state, setState] = useReducer(
    (state: State, newState: Partial<State>) => ({ ...state, ...newState }),
    {
      loading: false,
      otp: '',
      phoneNumber: initialPhoneNumber ?? '',
      email: '',
      tanLocked: false,
      tanIdentifierLocked: false,
      isTanCreated: false,
      counter: 0,
      needsEmailInput: false,
      showEmailInput: false,
      isEmailTanCreated: false
    }
  )

  const hasCreatedTan = useRef(false)

  useEffect(() => {
    if (instanceId === null || instanceId === undefined) {
      showError(translate('error'))
      back()
      return
    }
    if (
      initialPhoneNumber &&
      checkPhoneNumber(initialPhoneNumber) &&
      !hasCreatedTan.current
    ) {
      hasCreatedTan.current = true
      setState({ phoneNumber: initialPhoneNumber })
      void handleTanCreate('phone')
    }
    return () => clearTimer()
  }, [instanceId, initialPhoneNumber])

  const handleTanCreate = async (type: 'phone' | 'email'): Promise<void> => {
    const value = type === 'phone' ? state.phoneNumber : state.email
    if (!value || (type === 'phone' && !checkPhoneNumber(value))) {
      showError(translate('error.isdnError'))
      return
    }

    try {
      clearTimer()
      setState({ loading: true })

      const req: TanCreateRequest = {
        type,
        value,
        instanceId
      }

      const res = await createTanHandset(req)

      if (res == null) {
        showError(translate('leasing.corp.fail'))
        return
      }
      const {
        responseCode,
        response,
        responseDescription,
        email,
        danRedirectUrl
      } = res
      if (responseCode !== 'SUCCESS') {
        if (responseCode === 'TAN_CREATION_LOCKED') {
          setState({ tanRes: response, tanLocked: true })
        } else if (responseCode === 'TAN_IDENTIFIER_LOCKED') {
          setState({ tanRes: response, tanIdentifierLocked: true })
        } else {
          showError(translate('error.isdnError'))
        }
        return
      }
      if (
        type === 'phone' &&
        email &&
        responseDescription?.includes('Tan sent to')
      ) {
        setState({
          email: email || '',
          showEmailInput: true,
          isEmailTanCreated: true,
          tanRes: response,
          danRedirectUrl,
          counter: response.timeLeftInSec
        })
        setCounter(response.timeLeftInSec)
        startTimer()
      } else if (type === 'phone') {
        setState({
          email: email || '',
          needsEmailInput: true,
          tanRes: response,
          isTanCreated: true,
          danRedirectUrl
        })
        setCounter(response.timeLeftInSec)
        startTimer()
      } else {
        setState({
          tanRes: response,
          isTanCreated: true,
          danRedirectUrl
        })
        setCounter(response.timeLeftInSec)
        startTimer()
      }
    } catch (e) {
      console.error('TAN creation failed:', e)
      showError(translate('leasing.corp.fail'))
    } finally {
      setState({ loading: false, otp: '' })
    }
  }

  const handleTanConfirm = async (): Promise<void> => {
    if (!state.otp || state.otp.length < 6) {
      showError(translate('sim.tan_mismatch'))
      return
    }
    try {
      setState({ loading: true })
      const req: TanConfirmRequest = {
        type: 'phone',
        instanceId,
        code: state.otp
      }
      const res = await confirmTanHandset(req)
      if (res == null) {
        showError(translate('error'))
        return
      }
      const isBlacklist = res.errorMessage === 'BLACKLIST'
      const isUnderage = res.errorMessage === 'UNDERAGE'
      if (res.responseCode === 'SUCCESS') {
        if (isBlacklist || isUnderage) {
          setState({
            danRedirectUrl: res.danRedirectUrl,
            errorMessage: res.errorMessage
          })
          return
        }
        if (state.needsEmailInput) {
          setState({
            showEmailInput: true,
            isEmailTanCreated: false
          })
        } else {
          onClick({
            instanceId,
            phoneNumber: state.phoneNumber,
            email: state.email,
            danUrl: res.danRedirectUrl || ''
          })
        }
      } else {
        const msg =
          res.responseCode === 'TAN_MISMATCHED' ||
          res.responseCode === 'TAN_NOT_FOUND'
            ? translate('sim.tan_mismatch')
            : res.responseCode === 'TAN_LOCKED'
              ? translate('sim.tan_locked')
              : translate('leasing.corp.fail')
        showError(msg)
      }
    } catch (e) {
      console.error('TAN confirmation failed:', e)
      showError(translate('leasing.corp.fail'))
    } finally {
      setState({ loading: false })
    }
  }

  const startTimer = (): void => {
    const intervalId = setInterval(() => {
      setCounter((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId)
          setState({ timerId: undefined })
          return 0
        }
        return prev - 1
      })
    }, 1000)
    setState({ timerId: intervalId })
  }

  const clearTimer = (): void => {
    if (state.timerId != null) clearInterval(state.timerId)
    setState({ timerId: undefined })
    setCounter(0)
  }

  const renderTimer = (): string => {
    const min = Math.floor(counter / 60)
    const sec = counter % 60
    return `${min.toString().padStart(2, '0')}:${sec
      .toString()
      .padStart(2, '0')}`
  }

  const renderDuration = (time: number | undefined): string => {
    if (time === undefined || time === null) return '?'
    const hours = Math.floor(time / 3600)
    const minutes = Math.floor((time % 3600) / 60)
    const minutesStr = minutes < 10 ? '0' + minutes : minutes.toString()

    return locale === 'en'
      ? hours > 0
        ? `${hours} hours and ${minutesStr} minutes`
        : `${minutesStr} minutes`
      : hours > 0
        ? `${hours} цаг ${minutesStr} минутын`
        : `${minutesStr} минутын`
  }

  if (state.showEmailInput) {
    return (
      <EmailInput
        onClick={onClick}
        back={() => setState({ showEmailInput: false })}
        instanceId={instanceId}
        initialEmail={state.email}
        initialPhoneNumber={state.phoneNumber}
        isTanCreated={state.isEmailTanCreated}
        tanRes={state.tanRes}
        choosenPackage={choosenPackage}
      />
    )
  }

  if (state.errorMessage === 'UNDERAGE' || state.errorMessage === 'BLACKLIST') {
    return (
      <EMongolia
        instanceId={instanceId}
      />
    )
  }

  if (state.loading) {
    return (
      <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start relative no-scrollbar'>
        <div className='flex-grow px-4 py-4 sm:px-8 sm:py-8 overflow-auto no-scrollbar flex flex-col gap-4 items-center justify-center'>
          <Loader />
        </div>
        <div className='fixed md:sticky bottom-0 rounded-b-xl right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
          <div className='md:w-96 w-full flex gap-2'>
            <DarkButton type='outlined' onClick={back} fullwidth>
              {translate('back')}
            </DarkButton>
          </div>
        </div>
      </div>
    )
  }

  if (state.tanLocked || state.tanIdentifierLocked) {
    return (
      <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start relative no-scrollbar'>
        <div className='flex-grow px-4 py-4 sm:px-8 sm:py-8 overflow-auto no-scrollbar flex flex-col gap-4 items-center justify-center'>
          <p className='text-center w-2/3 text-textMid'>
            {translate('sim.tan_creation_locked').replace('$', renderDuration(state.tanRes?.creationLimit.lockTimeLeftSec))}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start relative no-scrollbar'>
      <div className='flex-grow px-4 py-4 sm:px-8 sm:py-8 overflow-auto no-scrollbar flex flex-col gap-4'>
        {state.isTanCreated
          ? (
            <div className='flex flex-col items-center justify-center gap-4 w-full'>
              <div className='w-full'>
                <TextField
                  label={translate('phoneNumber')}
                  variant='outlined'
                  size='small'
                  className='w-full rounded-lg text-textHigh'
                  value={state.phoneNumber}
                  onChange={(e) => setState({ phoneNumber: e.target.value })}
                  InputLabelProps={{
                    style: { color: 'var(--color-text-textHigh)' }
                  }}
                  InputProps={{
                    readOnly: true,
                    style: {
                      color: 'var(--color-text-textHigh)'
                    }
                  }}
                  sx={{
                    '& fieldset': {
                      borderRadius: '8px',
                      borderColor: checkPhoneNumber(state.phoneNumber ?? '')
                        ? 'var(--color-stroke-secondaryHighStroke)'
                        : 'var(--color-error-error)'
                    }
                  }}
                />
              </div>
              <p className='text-textMid font-medium text-sm text-center pt-24 whitespace-pre-line'>
                {translate('leasing.sent_phone').replace(
                  '$phoneNumber',
                  state.phoneNumber ?? ''
                )}
              </p>
              <div className='w-full flex justify-center h-fit'>
                <OTPInput
                  value={state.otp}
                  onChange={(otp) => setState({ otp })}
                  numInputs={6}
                  renderSeparator={<Divider orientation='vertical' />}
                  renderInput={(props) => <input {...props} />}
                  inputType='number'
                  inputStyle={{
                    width: '45px',
                    height: '45px',
                    fontSize: '18px',
                    fontWeight: '700',
                    borderRadius: '8px',
                    backgroundColor: 'surfaceInverse'
                  }}
                  containerStyle={{
                    border: '1px solid #CFD3DB',
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: 'surfaceInverse'
                  }}
                />
              </div>
              {(state.tanRes != null) && (
                <span className='text-textHigh text-sm'>{renderTimer()}</span>
              )}
              <DarkButton
                type='outlined'
                onClick={() => {
                  void handleTanCreate('phone')
                }}
                leadingIcon={<Loop />}
              >
                {translate('sim.tan.getagain')}
              </DarkButton>
            </div>
            )
          : (
            <div className='w-full'>
              <TextField
                label={translate('phoneNumber')}
                variant='outlined'
                size='small'
                className='w-full rounded-lg text-textHigh'
                value={state.phoneNumber}
                onChange={(e) => setState({ phoneNumber: e.target.value })}
                InputLabelProps={{
                  style: { color: 'var(--color-text-textHigh)' }
                }}
                InputProps={{
                  style: {
                    color: 'var(--color-text-textHigh)'
                  }
                }}
                sx={{
                  '& fieldset': {
                    borderRadius: '8px',
                    borderColor: checkPhoneNumber(state.phoneNumber ?? '')
                      ? 'var(--color-stroke-secondaryHighStroke)'
                      : 'var(--color-error-error)'
                  }
                }}
              />
            </div>
            )}
      </div>
      <div className='fixed md:sticky bottom-0 rounded-b-xl right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <div className='md:w-96 w-full flex gap-2'>
          {state.isTanCreated
            ? (
              <DarkButton
                onClick={handleTanConfirm}
                fullwidth
                disabled={!state.otp || state.otp.length < 6}
              >
                {translate('continue')}
              </DarkButton>
              )
            : (
              <DarkButton
                onClick={async () => await handleTanCreate('phone')}
                disabled={!checkPhoneNumber(state.phoneNumber ?? '')}
                fullwidth
              >
                {translate('continue')}
              </DarkButton>
              )}
        </div>
      </div>
    </div>
  )
}

export default NumberInput
